import { EventEmitter, Input, Output, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { Component, OnInit } from '@angular/core';
import {
  UntypedFormGroup,
  UntypedFormControl,
  Validators,
} from '@angular/forms';
import { faCalendar, faFaucet } from '@fortawesome/free-solid-svg-icons';
import { BAUP } from 'src/app/core/models/File';
import { DateService } from 'src/app/services/date.service';
import { LiabilitiesService } from 'src/app/services/liabilities.service';
import { ToastService } from 'src/app/services/toast.service';
import { ConfirmDialogSettings } from 'src/app/shared/confirm-dialog/confirm-dialog.component';
import { FileViewerSettings } from '../image-viewer/image-viewer.component';
import { FileParentEnum } from 'src/app/core/models/FileParentEnum';

@Component({
  selector: 'app-installment-requirement-form',
  templateUrl: './installment-requirement-form.component.html',
  styleUrls: ['./installment-requirement-form.component.css'],
})
export class InstallmentRequirementFormComponent implements OnInit, OnChanges {
  iconCalendar = faCalendar;
  iconPaymentService = faFaucet;

  @Input() installment: any;
  @Input() requirement: any;
  @Input() contractId?: number;

  @Output() closeAction = new EventEmitter();
  @Output() closePopover = new EventEmitter();

  confirmBoxTitles: ConfirmDialogSettings = {
    title: 'Confirmar Borrado de Requerimiento',
    subtitle: '',
  };

  installmentreqFormGroup = new UntypedFormGroup({
    amount: new UntypedFormControl('', [
      Validators.min(0),
      Validators.max(2147483647),
    ]),
    details: new UntypedFormControl(''),
    payDate: new UntypedFormControl(''),
  });

  insReqFiles: BAUP.File[];
  fileSettings: FileViewerSettings;

  constructor(
    private liabilityService: LiabilitiesService,
    private dateService: DateService,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.initializeForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['requirement']) {
      if (!changes['requirement'].firstChange) {
        setTimeout(() => {
          this.initializeForm();
          this.cdr.detectChanges();
        }, 0);
      } else {
        this.initializeForm();
      }
    }
    if (changes['installment'] && !changes['installment'].firstChange) {
      setTimeout(() => {
        this.initializeForm();
        this.cdr.detectChanges();
      }, 0);
    }
  }

  private getContractRequirementId(): number | null {
    const r = this.requirement;
    if (!r) return null;
    const raw =
      r.contractRequirementId ?? r.contractRequirmentId;
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }

  private getInstallmentRequirementRowId(): number | null {
    const r = this.requirement;
    if (!r) return null;
    const nested = r.installmentRequirement?.id;
    if (nested != null && nested !== '' && Number(nested) > 0) {
      return Number(nested);
    }
    const top = r.installmentRequirementId;
    const contractId = this.getContractRequirementId();
    if (
      top != null &&
      top !== '' &&
      Number(top) > 0 &&
      (contractId == null || Number(top) !== contractId)
    ) {
      return Number(top);
    }
    return null;
  }

  private initializeForm() {
    const req = this.requirement;
    if (req && req.hasInstallmentReq && req.installmentRequirement) {
      const ir = req.installmentRequirement;
      let date = ir.paymentDate ? ir.paymentDate : '';
      let details = ir.details ? ir.details : '';
      let amount = ir.amount !== undefined && ir.amount !== null ? ir.amount : '';

      this.installmentreqFormGroup.reset();
      this.installmentreqFormGroup.patchValue(
        {
          amount: amount,
          details: details,
          payDate: date ? this.dateService.transformToDateHash(new Date(date)) : '',
        },
        { emitEvent: false },
      );

      if (
        ir.installmentRequirementFiles &&
        Array.isArray(ir.installmentRequirementFiles)
      ) {
        this.insReqFiles = ir.installmentRequirementFiles.map((irf: any) => irf.file);
      } else {
        this.insReqFiles = [];
      }

      if (ir.id) {
        this.fileSettings = {
          title: 'Comprobantes de servicio',
          isDisplay: false,
          parent: FileParentEnum.INSTALLMENTREQUIREMENT,
          id: ir.id,
          contractId: this.contractId || this.installment?.contractId,
          modalComponent: true,
        };
      }
    } else if (req && req.hasInstallmentReq) {
      // Listado plano (installmentRequirementForList) sin objeto anidado
      const date = req.installmentRequirementDate || '';
      const details =
        req.instReqDetail != null && req.instReqDetail !== undefined
          ? req.instReqDetail
          : '';
      const amount =
        req.amount !== undefined && req.amount !== null ? req.amount : '';
      this.installmentreqFormGroup.reset();
      this.installmentreqFormGroup.patchValue(
        {
          amount: amount === 0 ? amount : amount || '',
          details,
          payDate: date
            ? this.dateService.transformToDateHash(new Date(date))
            : '',
        },
        { emitEvent: false },
      );
      this.insReqFiles = [];
      const rowId = this.getInstallmentRequirementRowId();
      if (rowId) {
        this.fileSettings = {
          title: 'Comprobantes de servicio',
          isDisplay: false,
          parent: FileParentEnum.INSTALLMENTREQUIREMENT,
          id: rowId,
          contractId: this.contractId || this.installment?.contractId,
          modalComponent: true,
        };
      }
    } else {
      this.insReqFiles = [];
      this.installmentreqFormGroup.reset({
        amount: '',
        details: '',
        payDate: '',
      });
    }
  }

  deleteDisabled(): boolean {
    return !this.requirement || !this.getInstallmentRequirementRowId();
  }

  createLiability(val) {
    console.log('createLiability llamado con valor:', val);
    console.log('Requirement:', this.requirement);
    console.log('Installment:', this.installment);
    
    if (!this.requirement || !this.installment) {
      console.error('Requirement o Installment no están disponibles');
      this.toastService.showToast('error', 'Error: Datos no disponibles');
      return;
    }

    const contractId = this.contractId || this.installment?.contractId;
    if (!contractId) {
      console.error('ContractId no está disponible');
      this.toastService.showToast('error', 'Error: No se puede identificar el contrato');
      return;
    }

    const contractRequirementId = this.getContractRequirementId();
    if (contractRequirementId == null) {
      console.error('ContractRequirementId no está disponible');
      this.toastService.showToast('error', 'Error: No se puede identificar el requerimiento');
      return;
    }

    let date = this.installmentreqFormGroup.value.payDate
      ? this.installmentreqFormGroup.value.payDate
      : null;

    const amountValue = this.installmentreqFormGroup.value.amount;
    const amount = amountValue === '' || amountValue === null || amountValue === undefined
      ? 0
      : Number(amountValue);

    const detailsValue = this.installmentreqFormGroup.value.details;
    const details = detailsValue === '' || detailsValue === null || detailsValue === undefined
      ? null
      : detailsValue;

    let installmentRequirement = {
      contractId: contractId,
      contractRequirementId: contractRequirementId,
      installmentId: this.installment.id,
      amount: amount,
      details: details,
      paymentDate: date ? this.dateService.transformToDateObject(
        date?.day,
        date?.month,
        date?.year,
      ) : null,
      installmentStatusId: val,
    };

    const rowId = this.getInstallmentRequirementRowId();

    if (this.requirement.hasInstallmentReq && rowId) {
      this.liabilityService
        .updateInstallmentRequirement(
          installmentRequirement,
          rowId,
        )
        .subscribe(
          (result: any) => {
            this.toastService.showToast('success', 'Pago de servicio actualizado correctamente');
            if (!this.requirement.installmentRequirement) {
              this.requirement.installmentRequirement = {} as any;
            }
            this.requirement.installmentRequirement.id = rowId;
            this.requirement.installmentRequirement.installmentStatusId = val;
            this.requirement.installmentRequirement.amount = amount;
            this.requirement.installmentRequirement.details = details;
            this.requirement.installmentRequirement.paymentDate = installmentRequirement.paymentDate;
            if (!this.requirement.installmentRequirement.installmentRequirementFiles) {
              this.requirement.installmentRequirement.installmentRequirementFiles = [];
            }
            if (this.requirement.status) {
              this.requirement.status.id = val;
            } else {
              this.requirement.status = { id: val } as any;
            }
            this.installmentreqFormGroup.markAsPristine();
            this.installmentreqFormGroup.markAsUntouched();
            setTimeout(() => {
              this.initializeForm();
              this.cdr.detectChanges();
              this.closeAction.emit();
              this.closePopover.emit();
            }, 50);
          },
          (error) => {
            console.error('Error al actualizar el pago de servicio:', error);
            this.toastService.showToast('error', 'Error al actualizar el pago de servicio');
          },
        );
    } else if (this.requirement.hasInstallmentReq && !rowId) {
      this.toastService.showToast(
        'warning',
        'No se pudo identificar el pago del servicio. Actualice la página e intente de nuevo.',
      );
    } else {
      this.liabilityService
        .createInstallmentRequirement(installmentRequirement)
        .subscribe(
          (result: any) => {
            this.toastService.showToast('success', 'Pago de servicio creado correctamente');
            this.requirement.hasInstallmentReq = true;
            if (!this.requirement.installmentRequirement) {
              this.requirement.installmentRequirement = {} as any;
            }
            this.requirement.installmentRequirement.id = result.installmentReqId;
            this.requirement.installmentRequirement.installmentStatusId = val;
            this.requirement.installmentRequirement.amount = amount;
            this.requirement.installmentRequirement.details = details;
            this.requirement.installmentRequirement.paymentDate = installmentRequirement.paymentDate;
            this.requirement.installmentRequirement.installmentRequirementFiles = [];
            if (this.requirement.status) {
              this.requirement.status.id = val;
            } else {
              this.requirement.status = { id: val } as any;
            }
            this.initializeForm();
            this.cdr.detectChanges();
            setTimeout(() => {
              this.closeAction.emit();
              this.closePopover.emit();
            }, 100);
          },
          (error) => {
            console.error('Error al crear el pago de servicio:', error);
            this.toastService.showToast('error', 'Error al crear el pago de servicio. Verifique que todos los campos estén completos.');
          },
        );
    }
  }

  deleteLiability() {
    console.log('deleteLiability llamado');
    console.log('Requirement:', this.requirement);
    
    const requirementId = this.getInstallmentRequirementRowId();

    if (!this.requirement || !requirementId) {
      console.error('No se puede eliminar: datos no disponibles');
      console.error('Requirement:', this.requirement);
      console.error('installmentRequirement:', this.requirement?.installmentRequirement);
      console.error('installmentRequirementId:', this.requirement?.installmentRequirementId);
      this.toastService.showToast('error', 'Error: No se puede eliminar el pago de servicio');
      return;
    }
    this.liabilityService
      .deleteInstallmentRequirement(requirementId)
      .subscribe(
        (result) => {
          this.toastService.showToast('success', 'Pago de servicio eliminado');
          if (this.requirement.installmentRequirement) {
            this.requirement.installmentRequirement.amount = 0;
            this.requirement.installmentRequirement.installmentStatusId = 2;
            this.requirement.installmentRequirement.details = null;
            this.requirement.installmentRequirement.paymentDate = null;
          }
          this.requirement.amount = 0;
          if (this.requirement.status) {
            this.requirement.status.id = 2;
          } else {
            this.requirement.status = { id: 2 } as any;
          }
          this.requirement.hasInstallmentReq = true;
          this.initializeForm();
          this.closeAction.emit();
          this.closePopover.emit();
        },
        (error) => {
          console.error('Error al eliminar el pago de servicio:', error);
          this.toastService.showToast('error', 'Error al eliminar el pago de servicio');
        }
      );
  }

  updateFileArray(result: BAUP.File[]) {
    const insReqFileArray = result.map((file) => {
      return {
        file: file,
        fileId: null,
        installmentRequirementId: null,
      };
    });
    if (!this.requirement.installmentRequirement) {
      this.requirement.installmentRequirement = {} as any;
    }
    this.requirement.installmentRequirement.installmentRequirementFiles =
      insReqFileArray;
  }
}
